"use client";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  Line,
  LineChart,
  XAxis,
  YAxis
} from "@cmp-src/primitives/chart";
import type { ReportQuery, ReportQueryResult } from "@gettactic/api";
import { useQueries } from "@tanstack/react-query";
import React from "react";
import { reportQueryOptions } from "../hooks/useReportQuery";
import { formatValue, partsUnit } from "../lib/formatValue";
import { CURRENT, PREVIOUS, type TrendPoint, TrendTooltip } from "./ChartBits";
import { ReportEmptyState } from "./ReportEmptyState";
import { ReportSkeleton } from "./ReportSkeleton";

/** How many members get a chart of their own. */
const MAX_MEMBERS = 8;

const config: ChartConfig = {
  value: { label: "Current", color: CURRENT },
  previous: { label: "Previous", color: PREVIOUS }
};

interface SmallMultiplesProps {
  /** The question, with exactly one breakdown. */
  query: ReportQuery;
  result: ReportQueryResult;
  onDrill: (dimension: string, memberId: string) => void;
}

/**
 * One small trend per member of the breakdown, the largest first, all on
 * the same scale so they compare at a glance. Each is the same question
 * pinned to that member; the header drills into it.
 */
export const SmallMultiples: React.FC<SmallMultiplesProps> = ({
  query,
  result,
  onDrill
}) => {
  const dimension = query.breakdown?.[0] ?? "";
  const measure = result.meta.measure;
  const unit = partsUnit(measure, query);
  const members = result.rows.slice(0, MAX_MEMBERS).map((row) => ({
    key: row.keys[0] ?? "",
    label: row.labels[0] ?? row.keys[0] ?? "",
    value: row.value
  }));
  const queries = useQueries({
    queries: members.map((member) =>
      reportQueryOptions({
        ...query,
        breakdown: [],
        filters: [
          ...(query.filters ?? []).filter((f) => f.dimension !== dimension),
          { dimension, in: [member.key] }
        ]
      })
    )
  });
  // One scale for every chart: the largest value anywhere.
  const max = Math.max(
    0.0001,
    ...queries.flatMap(
      (q) => q.data?.series.flatMap((p) => [p.value, p.previous ?? 0]) ?? []
    )
  );

  if (members.length === 0) {
    return (
      <ReportEmptyState
        title="Nothing in this period"
        hint="Try a longer period, or drop a filter that narrows it to nothing."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {members.map((member, index) => {
          const state = queries[index];
          const data: TrendPoint[] =
            state?.data?.series.map((p) => ({
              bucket: p.bucket,
              value: p.value,
              previous: p.previous ?? undefined,
              parts: p.parts
            })) ?? [];
          return (
            <div
              key={member.key}
              className="flex flex-col gap-1 rounded-lg border border-neutral-100 p-2.5"
            >
              <button
                type="button"
                onClick={() => onDrill(dimension, member.key)}
                title={`Drill into ${member.label}`}
                className="flex items-center justify-between gap-2 text-left hover:text-neutral-900"
              >
                <span className="truncate text-[12px] text-neutral-700">
                  {member.label}
                </span>
                <span className="shrink-0 font-semibold text-[13px] text-neutral-900 tabular-nums">
                  {formatValue(measure, member.value)}
                </span>
              </button>
              {state?.data ? (
                <ChartContainer
                  config={config}
                  className="aspect-auto h-[110px] w-full"
                >
                  <LineChart
                    data={data}
                    margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                    // Hovering one chart shows the same bucket on all of them.
                    syncId="small-multiples"
                    syncMethod="index"
                  >
                    <YAxis hide domain={[0, max]} />
                    <XAxis dataKey="bucket" hide />
                    <ChartTooltip
                      cursor={{ stroke: "#d4d4d4" }}
                      content={
                        <TrendTooltip
                          measure={measure}
                          unit={unit}
                          grain={state.data.meta.grain}
                        />
                      }
                    />
                    {state.data.totals.previous !== null ? (
                      <Line
                        dataKey="previous"
                        type="monotone"
                        stroke="var(--color-previous)"
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                        dot={false}
                        isAnimationActive={false}
                      />
                    ) : null}
                    <Line
                      dataKey="value"
                      type="monotone"
                      stroke="var(--color-value)"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ChartContainer>
              ) : state?.isError ? (
                <span className="py-10 text-center text-[11px] text-semantic-error-foreground">
                  Couldn't load
                </span>
              ) : (
                <ReportSkeleton kind="trend" className="h-[110px]" />
              )}
            </div>
          );
        })}
      </div>
      <span className="text-[11px] text-neutral-400">
        {result.rows.length > MAX_MEMBERS
          ? `Top ${MAX_MEMBERS} of ${result.rows.length}, on one scale · `
          : "On one scale · "}
        click a name to drill into it
      </span>
    </div>
  );
};
